import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LogoPagaliImg from '../assets/logo-pagali-color@2x.png';
import { metaPixel } from '../utils/pixel';

const EVENTOS: Record<string, {
  nome: string;
  subtitulo: string;
  local: string;
  data: string;
  horario: string;
  premio: string;
  descricaoPremio: string;
  cor: string;
}> = {
  cvma2026: {
    nome: 'CVMA 2026',
    subtitulo: 'Cape Verde Music Awards',
    local: 'Mindelo, São Vicente',
    data: '6 de Junho de 2026',
    horario: '20h00 – 22h00',
    premio: '500 Pontos Pagali + Tote Bag Pagali',
    descricaoPremio: 'Regista-te agora e participa no sorteio exclusivo do evento. Um sortudo leva 500 pontos Pagali e uma Tote Bag Pagali oficial.',
    cor: '#6C3FC5',
  },
};

const EventoLanding: React.FC = () => {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const evento = EVENTOS[codigo || ''];

  useEffect(() => {
    // Remove o Smart App Banner do iOS nesta página — não queremos que o utilizador
    // seja redirecionado para a App Store ao abrir a landing do evento via QR code.
    const metaTag = document.querySelector('meta[name="apple-itunes-app"]');
    if (metaTag) metaTag.remove();

    if (evento) {
      metaPixel.track('ViewContent', {
        content_name: evento.nome,
        content_category: 'evento',
        currency: 'CVE',
      });
    }
  }, [evento]);

  if (!evento) {
    return (
      <div style={styles.errorContainer}>
        <img src={LogoPagaliImg} alt="Pagali" style={styles.logo} />
        <p style={styles.errorText}>Evento não encontrado.</p>
        <button style={styles.primaryBtn} onClick={() => navigate('/')}>Ir para o início</button>
      </div>
    );
  }

  const registerUrl = `/register/form?evento=${codigo}`;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{ ...styles.header, background: `linear-gradient(135deg, ${evento.cor} 0%, #1a0a3e 100%)` }}>
        <img src={LogoPagaliImg} alt="Pagali" style={styles.logo} />
        <div style={styles.eventBadge}>EVENTO EXCLUSIVO</div>
        <h1 style={styles.eventName}>{evento.nome}</h1>
        <p style={styles.eventSub}>{evento.subtitulo}</p>

        <div style={styles.metaRow}>
          <div style={styles.metaItem}>
            <span style={styles.metaIcon}>📍</span>
            <span>{evento.local}</span>
          </div>
          <div style={styles.metaItem}>
            <span style={styles.metaIcon}>📅</span>
            <span>{evento.data}</span>
          </div>
          <div style={styles.metaItem}>
            <span style={styles.metaIcon}>🕗</span>
            <span>{evento.horario}</span>
          </div>
        </div>
      </div>

      {/* Prémio */}
      <div style={styles.prizeCard}>
        <div style={styles.prizeIcon}>🎁</div>
        <h2 style={styles.prizeTitle}>Sorteio Exclusivo</h2>
        <p style={styles.prizeValue}>{evento.premio}</p>
        <p style={styles.prizeDesc}>{evento.descricaoPremio}</p>
      </div>

      {/* Como participar */}
      <div style={styles.stepsSection}>
        <h3 style={styles.stepsTitle}>Como participar?</h3>
        <div style={styles.steps}>
          <div style={styles.step}>
            <div style={styles.stepNum}>1</div>
            <div>
              <strong>Baixa a app Pagali</strong>
              <p style={styles.stepDesc}>Disponível no App Store e Google Play</p>
            </div>
          </div>
          <div style={styles.step}>
            <div style={styles.stepNum}>2</div>
            <div>
              <strong>Cria a tua conta</strong>
              <p style={styles.stepDesc}>Registo gratuito, em menos de 2 minutos</p>
            </div>
          </div>
          <div style={styles.step}>
            <div style={styles.stepNum}>3</div>
            <div>
              <strong>Estás dentro!</strong>
              <p style={styles.stepDesc}>O sorteio é feito no final do evento</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div style={styles.ctaSection}>
        <p style={styles.ctaLabel}>Regista-te agora e participa</p>

        {/* Web registration */}
        <button style={styles.primaryBtn} onClick={() => navigate(registerUrl)}>
          Criar conta Pagali
        </button>

        <p style={styles.orLabel}>ou descarrega a app</p>

        <div style={styles.storeRow}>
          <a
            href="https://apps.apple.com/cv/app/pagali/id6449363463"
            style={styles.storeBtn}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg style={styles.storeLogo} viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            App Store
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=cv.isone.pagali"
            style={{ ...styles.storeBtn, background: '#01875f' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg style={styles.storeLogo} viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.18 23.76c.33.18.71.2 1.07.04l12.3-6.96-2.6-2.6-10.77 9.52zM.44 1.06C.17 1.4 0 1.88 0 2.49v19.03c0 .61.17 1.09.44 1.43l.07.07 10.66-10.66v-.25L.51.99.44 1.06zM20.55 10.38l-2.76-1.56-2.93 2.93 2.93 2.93 2.78-1.57c.79-.45.79-1.18-.02-1.73zM4.25.2L16.55 7.16l-2.6 2.6L3.18.24C3.52.08 3.9.1 4.25.2z"/>
            </svg>
            Google Play
          </a>
        </div>
      </div>

      {/* Features */}
      <div style={styles.featuresSection}>
        <h3 style={styles.featuresTitle}>O que é o Pagali?</h3>
        <div style={styles.featuresGrid}>
          {[
            { icon: '🛍️', label: 'Pagali Shop', desc: 'Compra produtos e serviços de entidades cabo-verdianas' },
            { icon: '🎫', label: 'Pagali Pass', desc: 'Acede a eventos e experiências exclusivas' },
            { icon: '⭐', label: 'Pontos & Prémios', desc: 'Cada compra vale pontos. Quanto mais usas, mais ganhas' },
            { icon: '🎓', label: 'Academia', desc: 'Aprende a gerir melhor o teu dinheiro e negócio' },
          ].map((f) => (
            <div key={f.label} style={styles.featureCard}>
              <span style={styles.featureIcon}>{f.icon}</span>
              <strong style={styles.featureLabel}>{f.label}</strong>
              <p style={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.footer}>
        <img src={LogoPagaliImg} alt="Pagali" style={styles.footerLogo} />
        <p style={styles.footerText}>pagali.cv — A plataforma digital de Cabo Verde</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f7f6fb',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflowX: 'hidden',
  },
  header: {
    padding: '40px 24px 48px',
    textAlign: 'center',
    color: '#fff',
  },
  logo: {
    height: 36,
    marginBottom: 20,
    filter: 'brightness(0) invert(1)',
  },
  eventBadge: {
    display: 'inline-block',
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.4)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    padding: '4px 12px',
    borderRadius: 20,
    marginBottom: 16,
  },
  eventName: {
    fontSize: 40,
    fontWeight: 900,
    margin: '0 0 6px',
    letterSpacing: -1,
    color: '#ffffff',
  },
  eventSub: {
    fontSize: 16,
    color: '#ffffff',
    opacity: 0.85,
    margin: '0 0 28px',
  },
  metaRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'center',
  },
  metaItem: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    fontSize: 14,
    opacity: 0.9,
  },
  metaIcon: {
    fontSize: 16,
  },
  prizeCard: {
    background: '#fff',
    margin: '-24px 20px 0',
    borderRadius: 16,
    padding: '28px 24px',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(108,63,197,0.15)',
    position: 'relative',
    zIndex: 1,
  },
  prizeIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  prizeTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a0a3e',
    margin: '0 0 8px',
  },
  prizeValue: {
    fontSize: 22,
    fontWeight: 800,
    color: '#6C3FC5',
    margin: '0 0 12px',
  },
  prizeDesc: {
    fontSize: 14,
    color: '#666',
    lineHeight: 1.5,
    margin: 0,
  },
  stepsSection: {
    padding: '32px 20px 0',
  },
  stepsTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a0a3e',
    margin: '0 0 20px',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  step: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    background: '#fff',
    padding: '16px',
    borderRadius: 12,
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
  },
  stepNum: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#6C3FC5',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepDesc: {
    fontSize: 13,
    color: '#888',
    margin: '4px 0 0',
  },
  ctaSection: {
    padding: '32px 20px',
    textAlign: 'center',
  },
  ctaLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #6C3FC5, #9B59B6)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 20,
  },
  orLabel: {
    fontSize: 13,
    color: '#999',
    margin: '0 0 16px',
  },
  storeRow: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
  storeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    background: '#1a0a3e',
    color: '#fff',
    borderRadius: 10,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
  },
  storeLogo: {
    width: 20,
    height: 20,
  },
  featuresSection: {
    padding: '0 20px 32px',
  },
  featuresTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a0a3e',
    margin: '0 0 16px',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  featureCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '16px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  featureIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  featureLabel: {
    fontSize: 13,
    color: '#1a0a3e',
  },
  featureDesc: {
    fontSize: 12,
    color: '#888',
    margin: 0,
    lineHeight: 1.4,
  },
  footer: {
    padding: '24px',
    textAlign: 'center',
    borderTop: '1px solid #eee',
  },
  footerLogo: {
    height: 24,
    marginBottom: 8,
    opacity: 0.5,
  },
  footerText: {
    fontSize: 12,
    color: '#aaa',
    margin: 0,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: 24,
    gap: 16,
  },
  errorText: {
    color: '#666',
  },
};

export default EventoLanding;
